import { useEffect } from 'react';

import { Loader2 } from 'lucide-react';

import type { CFSReportContext } from '@/pages/CFS/reports/cfsReport/types';

import type { CFSRedactionsMap } from '../data';
import { useCFSPDF } from '../hooks/useCFSPDF';

interface PDFPreviewPageProps {
  ctx: CFSReportContext;
  redactions: CFSRedactionsMap;
  onPDFGenerated?: (blob: Blob | null, url: string | null) => void;
}

const PDFPreviewPage = ({
  ctx,
  redactions,
  onPDFGenerated,
}: PDFPreviewPageProps) => {
  const { pdfBlob, pdfUrl, loading, error } = useCFSPDF({ ctx, redactions });

  useEffect(() => {
    if (pdfBlob && pdfUrl && onPDFGenerated) onPDFGenerated(pdfBlob, pdfUrl);
  }, [pdfBlob, pdfUrl, onPDFGenerated]);

  return (
    <div className="h-full w-full flex flex-col p-3">
      <div className="mb-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Print Preview
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Review the document layout exactly as it will appear in the generated
          PDF
        </p>
      </div>

      {loading && (
        <div className="flex-1 flex items-center justify-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-gray-600 dark:text-gray-400" />
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Generating PDF preview...
            </p>
          </div>
        </div>
      )}

      {error && !loading && (
        <div className="flex-1 flex items-center justify-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800">
          <div className="flex flex-col items-center gap-3 text-red-600">
            <p className="text-sm font-medium">Failed to generate PDF</p>
            <p className="text-xs">{error}</p>
          </div>
        </div>
      )}

      {pdfUrl && !loading && !error && (
        <div className="flex-1 border border-gray-300 dark:border-gray-600 rounded overflow-hidden bg-white dark:bg-gray-800">
          <embed
            src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            type="application/pdf"
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          />
        </div>
      )}
    </div>
  );
};

export default PDFPreviewPage;
